/**
 * Cache keys: what makes two reads "the same read".
 *
 * A key that is too loose hands one user another user's rows. A key that is too
 * tight never hits. Every part below earns its place because leaving it out
 * causes a specific, observed failure — the reasons are written down rather than
 * implied, because a future "simplification" here is a data leak.
 *
 * Shape: `v1:<user>:<branch>:<roles>:GET <url>?<canonical params>`
 */

import { getActivePinia } from "pinia";
import { getAccessSnapshot } from "@/helper/permissionHelper";
import { currentUserKey } from "@/offline/user";

/** Bump to discard every stored entry at once (a shape change, a bad key scheme). */
export const CACHE_KEY_VERSION = "v1";

/**
 * Headers that steer error reporting, not the answer. Two callers asking the same
 * question — one wanting a toast, one not — must share a single entry.
 */
const IGNORED_HEADERS = [
  "X-Suppress-Error-Toast",
  "X-Suppress-Authorization-Banner",
  "X-Suppress-Action-Loader",
];

/**
 * The branch the backend will scope this read to.
 *
 * ⚠️ NO READ SENDS A BRANCH. The server infers it from the session, and a user can
 * switch branch mid-session, so the same URL returns different rows at different
 * moments. Without this in the key, two clinics on one machine read each other's
 * data.
 *
 * Read through the active Pinia state rather than importing the store, for the same
 * reason `plugins/1.router` does: the store sits above this layer and importing it
 * here would put the cache inside the startup import cycle.
 */
function activeBranch(): string {
  try {
    const state = getActivePinia()?.state?.value as Record<string, any> | undefined;

    return String(state?.branch?.current ?? "").trim() || "-";
  }
  catch {
    return "-";
  }
}

/**
 * A short fingerprint of the user's roles.
 *
 * ⚠️ A CACHED RESPONSE IS PERMISSION-SHAPED, NOT DOCTYPE-SHAPED. Phone numbers are
 * blanked in the API mappers according to the CURRENT user's roles
 * (`redactGuardianPhone` and friends), so the stored body already carries "hidden
 * for this viewer". Sharing it across roles either leaks a number to someone barred
 * from it, or serves permanent blanks to someone entitled to see it.
 */
function roleFingerprint(): string {
  const roles = getAccessSnapshot()?.roles ?? [];

  if (!roles.length)
    return "none";

  // Order-independent and cheap; this is a cache key, not a security boundary.
  const sorted = [...roles].map(role => role.trim().toLowerCase()).sort();
  let hash = 0;

  for (const role of sorted) {
    for (let i = 0; i < role.length; i += 1)
      hash = (hash * 31 + role.charCodeAt(i)) | 0;
  }

  return (hash >>> 0).toString(36);
}

/**
 * Params in a stable order, so `{a,b}` and `{b,a}` are one entry.
 *
 * Values are stringified as the wire sees them: Frappe filters arrive as
 * pre-serialised JSON strings, so no re-ordering is attempted inside them — two
 * differently-ordered filter strings are genuinely two different queries as far as
 * the server is concerned.
 */
export function canonicalParams(params: unknown): string {
  if (!params || typeof params !== "object")
    return "";

  const entries = Object.entries(params as Record<string, unknown>)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => [key, typeof value === "object" ? JSON.stringify(value) : String(value)] as const)
    .sort(([a], [b]) => a.localeCompare(b));

  return entries.map(([key, value]) => `${key}=${value}`).join("&");
}

export interface CacheKeyInput {
  url: string;
  params?: unknown;
  method?: string;
}

/** The identity a stored entry is filed under. */
export function buildCacheKey(input: CacheKeyInput): string {
  const method = String(input.method ?? "GET").toUpperCase();
  const user = currentUserKey() || "-";
  const query = canonicalParams(input.params);

  return [
    CACHE_KEY_VERSION,
    user,
    activeBranch(),
    roleFingerprint(),
    `${method} ${input.url}${query ? `?${query}` : ""}`,
  ].join(":");
}

/** Strip the control headers before they can reach a key by accident. */
export function cacheableHeaders(headers: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!headers)
    return {};

  const copy = { ...headers };

  for (const header of IGNORED_HEADERS)
    delete copy[header];

  return copy;
}

/**
 * Is the signed-in user's access resolved enough to trust what comes back?
 *
 * ⚠️ REDACTION FAILS CLOSED WHILE PERMISSIONS ARE UNRESOLVED. During the router's
 * access-load window the snapshot's source is `"empty"`, every mapper blanks phone
 * numbers, and a response fetched then looks identical to one legitimately redacted.
 * Storing it would make those blanks permanent for a user who is allowed to see the
 * numbers, and nothing would ever correct it. So: do not store anything until we
 * know who we are.
 */
export function permissionsResolved(): boolean {
  const snapshot = getAccessSnapshot();

  if (!snapshot)
    return false;

  return snapshot.source !== "empty" && (snapshot.roles?.length ?? 0) > 0;
}

/** The user a key belongs to, for purging one user's entries on sign-out. */
export function userOfKey(key: string): string {
  return key.split(":")[1] ?? "";
}
